import { useContext, useMemo } from 'react';

import { FilterOptions, Sort } from '@vimeo/comments-ui';
import { secondsToDisplay } from '@vimeo/core/libs/date';
import { GetAlbumVideoCommentsFields } from '@vimeo/gctl-api/fetchers/album/video/comments';
import { GetVideoCommentsFields } from '@vimeo/gctl-api/fetchers/video/comments';
import { useGetAlbumVideoCommentsInfinite } from '@vimeo/gctl-api/hooks/useAlbumVideoComments';
import { useGetVideoCommentsInfinite } from '@vimeo/gctl-api/hooks/useVideoComments';
import { useGetVideoPrivateCommentsInfinite } from '@vimeo/gctl-api/hooks/useVideoPrivateComments';
import { Comment } from '@vimeo/gctl-api/models/components/schemas/comment/Comment';
import { PrivateCommentReply } from '@vimeo/gctl-api/models/components/schemas/private-comment-reply/PrivateCommentReply';
import { PrivateComments } from '@vimeo/gctl-api/models/components/schemas/private-comments/PrivateComments';
import { Reply } from '@vimeo/gctl-api/models/components/schemas/reply/Reply';
import copyToClipboard from '@vimeo/ui/libs/copyToClipboard';
import { getPasswordHashFromCookie } from '@vimeo/use-unlocked-video';

import { PUBLIC_COMMENTS_FIELDS } from '../../utils/constants';
import {
  checkIsOwnComment,
  getPublicCommentsQuery,
  getReviewPasswordHashFromCookie,
  getShowcasePasswordHashFromCookie,
  idFromUri,
} from '../../utils/helper';
import { guestLoginModalContext } from '../use-guestlogin-modal';
import { useUserPermissions } from '../use-user-permission';
import { useViewer } from '../use-viewer';

/**
 * A customized hook to handle fetching Public and Private comments data depending on privacy
 */
export function useGetComments(
  clipRequestId: string,
  isPublic: boolean,
  sort?: Sort,
  filterOptions?: FilterOptions,
  showcaseId?: number,
  reviewId?: string,
) {
  const useCommentsHook = isPublic ? usePublicComments : usePrivateComments;
  return useCommentsHook(clipRequestId, sort, filterOptions, showcaseId, reviewId);
}

/**
 * A hook to handle fetching Public comments data
 */
export function usePublicComments(
  clipRequestId: string,
  sort?: Sort,
  filterOptions?: FilterOptions,
  showcaseId?: number,
) {
  const viewer = useViewer();
  const { isOwner, canDelete } = useUserPermissions(clipRequestId, showcaseId);
  const { guestUser } = useContext(guestLoginModalContext);
  const guestUserId = guestUser ? JSON.parse(guestUser)?.guestUserId : null;

  const videoComments = useGetVideoCommentsInfinite(
    () => {
      if (!clipRequestId || showcaseId) return null;
      const password = getPasswordHashFromCookie(clipRequestId);
      return {
        where: { videoId: clipRequestId as unknown as number },
        select: PUBLIC_COMMENTS_FIELDS as GetVideoCommentsFields,
        query: getPublicCommentsQuery(password, sort, filterOptions),
      };
    },
    { parallel: true },
  );

  const albumVideoComments = useGetAlbumVideoCommentsInfinite(() => {
    if (!clipRequestId || !showcaseId) return null;
    const password = getShowcasePasswordHashFromCookie(showcaseId);
    return {
      where: { albumId: showcaseId, videoId: clipRequestId as unknown as number },
      select: PUBLIC_COMMENTS_FIELDS as GetAlbumVideoCommentsFields,
      query: getPublicCommentsQuery(password, sort, filterOptions),
    };
  });

  const {
    data: commentsData,
    mutate,
    isLoading,
    error,
    size,
    setSize,
  } = showcaseId ? albumVideoComments : videoComments;

  const loadMoreComments = (pageSize?: number) => {
    setSize(pageSize ?? size + 1);
  };

  const isDone = !commentsData?.[commentsData?.length - 1]?.paging.next;
  const isLoadingInitialData = !commentsData && !error;
  const isLoadingMore =
    isLoadingInitialData ||
    (size > 0 && commentsData && typeof commentsData[size - 1] === 'undefined');

  const publicComments = useMemo(() => {
    const parseComment = (comment: Comment | Reply) => {
      const isOwnComment = checkIsOwnComment(
        viewer?.user?.uri,
        guestUserId,
        comment.metadata?.connections?.user?.uri,
        comment.metadata?.connections?.guestUser?.uri,
      );

      return {
        text: comment.richtext ?? comment.text,
        plainText: comment.text,
        user: {
          name:
            comment.metadata?.connections?.user?.name ||
            comment.metadata?.connections?.guestUser?.name ||
            null,
          link:
            !viewer?.isFromCopyrightRestrictedRegion && comment.metadata?.connections?.user?.link
              ? comment.metadata?.connections?.user?.link
              : null,
          pictures: comment.metadata?.connections?.user?.pictures || null,
          id: comment.metadata?.connections?.user?.uri || null,
          badge: comment.metadata?.connections?.user?.badge || null,
          isStaffPicked: comment.metadata?.connections?.user?.isStaffPicked || false,
          uri: comment.metadata?.connections?.user?.uri || null,
        },
        id: comment.uri,
        createdTime: comment.createdOn,
        isRichComment: !!comment.richtext,
        isOwnVideo: isOwner || canDelete,
        isOwnComment,
        textDecorations: comment.textDecorations,
        deletedOn: comment.deletedOn,
        editedTime: comment.lastEditedOn ?? undefined,
      };
    };

    return commentsData
      ?.filter((page) => !!page)
      ?.flatMap((page) => page.data.flatMap((comment) => comment))
      ?.map((comment) => {
        return {
          ...parseComment(comment),
          replies: comment.replies?.map((reply) => {
            return parseComment(reply);
          }),
        };
      });
  }, [
    commentsData,
    isOwner,
    canDelete,
    viewer?.user,
    viewer?.isFromCopyrightRestrictedRegion,
    guestUserId,
  ]);

  const copyComment = (id: string, parentCommentId: string = '') => {
    const vimeoHttpsUrl = viewer?.vimeoHttpsUrl;
    const commentId = parentCommentId ? parentCommentId : id;
    const commentQuery = `comment=${idFromUri(commentId)}`;
    let baseUri = showcaseId
      ? `/showcase/${showcaseId}?video=${clipRequestId.split?.(':')?.[0]}&${commentQuery}`
      : `/${clipRequestId.replace(':', '/')}?${commentQuery}`;
    if (parentCommentId) {
      baseUri = baseUri.concat(`&reply=${idFromUri(id)}`);
    }
    copyToClipboard(`${vimeoHttpsUrl}${baseUri}`);
  };

  return {
    comments: publicComments,
    // @ts-expect-error GCTL doesn't have a type for filteredTotal on the pager
    totalComments: commentsData ? commentsData[0]?.filteredTotal : 0,
    commentsLoading: isLoading,
    commentsError: error,
    revalidateComments: mutate,
    loadMoreComments,
    isCommentsDone: isDone,
    isLoadingMoreComments: isLoadingMore,
    page: size,
    copyComment,
  };
}

/**
 * A hook to handle fetching Private comments data
 */
export function usePrivateComments(
  clipRequestId: string,
  sort?: Sort,
  filterOptions?: FilterOptions,
  showcaseId?: number,
  reviewId?: string,
) {
  const { isOwner, canDelete, canReactToCollabComments } = useUserPermissions(
    clipRequestId,
    undefined,
    reviewId,
  );
  const viewer = useViewer();
  const password = getReviewPasswordHashFromCookie(reviewId);
  const { guestUser } = useContext(guestLoginModalContext);
  const guestUserId = guestUser ? JSON.parse(guestUser)?.guestUserId : null;

  const reactionFields = canReactToCollabComments
    ? ([
        'reactions.reactionCode',
        'reactions.uri',
        'reactions.isByCurrentUser',
        'reactions.userDisplayName',
        'replies.reactions.reactionCode',
        'replies.reactions.uri',
        'replies.reactions.isByCurrentUser',
        'replies.reactions.userDisplayName',
      ] as const)
    : ([] as const);

  const {
    data: privateCommentsData,
    isLoading: privateCommentsLoading,
    error: privateCommentsError,
    size,
    setSize,
    mutate,
  } = useGetVideoPrivateCommentsInfinite(
    () => {
      if (!clipRequestId) return null;
      return {
        where: { videoId: clipRequestId as unknown as number },
        select: [
          'metadata.connections.replies.total',
          'text',
          'richtext',
          'uri',
          'metadata.connections.user',
          'createdTime',
          'replies.text',
          'replies.richtext',
          'replies.createdTime',
          'replies.metadata.connections.user',
          'replies.uri',
          'replies.userName',
          'replies.lastEditedTime',
          'replies.guestUserUri',
          'metadata',
          'timeCode',
          'userName',
          'status',
          'textDecorations',
          'deletedOn',
          'lastEditedTime',
          'coordinates',
          'guestUserUri',
          ...reactionFields,
        ],
        query: {
          includeDeletedComments: true,
          sort: sort?.type,
          direction: sort?.direction,
          status: filterOptions?.status,
          commented_user_type: ['team', 'non_team'].includes(filterOptions?.userType || '')
            ? filterOptions?.userType
            : undefined,
          mentions_and_replies: filterOptions?.mentionAndReplies || undefined,
          versionUri: filterOptions?.videoVersionUri || undefined,
          reviewId,
          password,
        },
        headers: {
          ...(filterOptions?.targetApiVersion
            ? { Accept: `application/vnd.vimeo.*+json;version=${filterOptions.targetApiVersion}` }
            : {}),
        },
      };
    },
    {
      parallel: true,
    },
  );

  const loadMoreComments = (pageSize?: number) => {
    setSize(pageSize ?? size + 1);
  };

  const isDone = !privateCommentsData?.[privateCommentsData?.length - 1]?.paging.next;
  const isLoadingInitialData = !privateCommentsData && !privateCommentsError;
  const isLoadingMore =
    isLoadingInitialData ||
    (size > 0 && privateCommentsData && typeof privateCommentsData[size - 1] === 'undefined');

  const privateComments = useMemo(() => {
    const parseComment = (comment: PrivateCommentReply | PrivateComments) => {
      const isOwnComment = checkIsOwnComment(
        viewer?.user?.uri,
        guestUserId,
        comment.metadata?.connections?.user?.uri,
        comment?.guestUserUri,
      );
      return {
        text: comment.richtext ?? comment.text,
        textDecorations: comment.textDecorations,
        plainText: comment.text,
        user: {
          name: comment.userName || null,
          link:
            !viewer?.isFromCopyrightRestrictedRegion && comment.metadata?.connections?.user?.link
              ? comment.metadata.connections?.user?.link
              : null,
          pictures: comment.metadata?.connections?.user?.pictures || null,
          id: comment.metadata?.connections?.user?.uri || null,
          isStaffPicked: comment.metadata?.connections?.user?.isStaffPicked || false,
          uri: comment.metadata?.connections?.user?.uri || null,
        },
        id: comment.uri,
        createdTime: comment.createdTime,
        isRichComment: !!comment.richtext,
        isOwnVideo: isOwner || canDelete,
        isOwnComment,
        status: comment.status,
        deletedOn: comment.deletedOn,
        editedTime: comment.lastEditedTime ?? undefined,
        coordinates: comment.coordinates,
        reactions: comment.reactions ?? [],
      };
    };

    return (
      privateCommentsData
        ?.filter((page) => !!page)
        ?.flatMap((page) => page.data.flatMap((comment) => comment))
        ?.map((comment) => {
          return {
            timeCode: comment.timeCode ? comment.timeCode : null,
            formattedTimeCode: comment.timeCode ? secondsToDisplay(comment.timeCode) : null,
            replies:
              comment.replies?.map((reply) => parseComment(reply as PrivateCommentReply)) || [],
            ...parseComment(comment as PrivateComments),
          };
        }) || []
    );
  }, [
    isOwner,
    canDelete,
    privateCommentsData,
    viewer?.user,
    viewer?.isFromCopyrightRestrictedRegion,
    guestUserId,
  ]);

  const copyComment = (id: string, parentCommentId: string = '') => {
    const vimeoHttpsUrl = viewer?.vimeoHttpsUrl;
    const commentId = parentCommentId ? parentCommentId : id;
    let baseUri = `${window.location.pathname}?comment=${idFromUri(commentId)}`;
    if (parentCommentId) {
      baseUri = baseUri.concat(`&reply=${idFromUri(id)}`);
    }
    copyToClipboard(`${vimeoHttpsUrl}${baseUri}`);
  };

  return {
    comments: privateComments,
    totalComments: privateCommentsData ? privateCommentsData[0]?.total : 0,
    commentsLoading: privateCommentsLoading || isLoadingInitialData,
    commentsError: privateCommentsError,
    revalidateComments: mutate,
    loadMoreComments,
    isCommentsDone: isDone,
    isLoadingMoreComments: isLoadingMore,
    page: size,
    copyComment,
  };
}
